/*
 * English UI strings for the web app. Source of truth for which keys exist —
 * `pt-BR.ts` must define the exact same key set (enforced by strings.test.ts).
 * `{{param}}` placeholders are interpolated by `t()` / `translate()`.
 */
const en: Record<string, string> = {
  // Common
  "common.retry": "Retry",
  "common.cancel": "Cancel",
  "common.close": "Close",
  "common.back": "Back",
  "common.save": "Save",
  "common.delete": "Delete",
  "common.done": "Done",
  "common.loading": "Loading…",
  "common.loadingMore": "Loading more…",
  "common.couldntLoadMore": "Couldn't load more.",
  "common.backToLibrary": "Back to Library",
  "common.findSomething": "Find something to watch",
  "common.optional": "(optional)",
  "common.film": "Film",
  "common.series": "Series",
  "common.viewProfile": "{{name}}, view profile",
  "common.openTitle": "Open {{name}}",
  "common.avatarOf": "Avatar of {{name}}",
  "common.skipToContent": "Skip to content",
  "common.notBuiltYet": "Not built yet — this screen is stubbed while the web app foundation ships.",

  // Relative time
  "time.justNow": "just now",
  "time.minutesAgo": "{{count}}m ago",
  "time.hoursAgo": "{{count}}h ago",
  "time.daysAgo": "{{count}}d ago",

  // Theme
  "theme.label": "Theme",
  "theme.light": "Light",
  "theme.system": "System",
  "theme.dark": "Dark",

  // Navigation
  "nav.primary": "Primary",
  "nav.library": "Library",
  "nav.discover": "Discover",
  "nav.friends": "Friends",
  "nav.alerts": "Alerts",
  "nav.profile": "Profile",
  "nav.accountMenu": "Account menu for {{name}}",
  "nav.viewProfile": "View profile",
  "nav.importExport": "Import / export",
  "nav.unreadBadge": "{{label}}, {{count}} unread notifications",

  // Library
  "library.title": "Your Library",
  "library.overline": "Library",
  "library.greeting.morning": "Good morning",
  "library.greeting.afternoon": "Good afternoon",
  "library.greeting.evening": "Good evening",
  "library.filterByStatus": "Filter by status",
  "library.filter.all": "All",
  "library.filter.watchlist": "Watchlist",
  "library.filter.watching": "Watching",
  "library.filter.completed": "Completed",
  "library.filter.dropped": "Dropped",
  "library.filterByType": "Filter by type",
  "library.type.all": "Everything",
  "library.type.series": "Series",
  "library.type.movies": "Films",
  "library.sortBy": "Sort by",
  "library.sort.recent": "Recent",
  "library.sort.title": "Title",
  "library.sort.progress": "Progress",
  "library.collectionView": "Collection view",
  "library.viewAsGrid": "Grid view",
  "library.viewAsList": "List view",
  "library.loading": "Loading your library",
  "library.empty.all.overline": "Your shelf",
  "library.empty.all.body": "Your library is empty — add shows and films from Discover.",
  "library.empty.watchlist.overline": "Watchlist",
  "library.empty.watchlist.body": "Your watchlist is empty — save titles to watch later from Discover.",
  "library.empty.watching.overline": "Watching",
  "library.empty.watching.body": "Nothing in progress — pick your next watch from Discover.",
  "library.empty.completed.overline": "Completed",
  "library.empty.completed.body": "Nothing finished yet — titles you complete will land here.",
  "library.empty.dropped.overline": "Dropped",
  "library.empty.dropped.body": "Nothing dropped — titles you stop watching will land here.",
  "library.error.load": "Couldn't load your library",
  "library.status.watchlist": "Watchlist",
  "library.status.watching": "Watching",
  "library.status.completed": "Completed",
  "library.status.dropped": "Dropped",
  "library.status.rated": "Rated {{rating}} of 5",

  // Show detail
  "show.tabs.sectionsA11y": "Title sections",
  "show.tabs.details": "Details",
  "show.tabs.discussion": "Discussion",
  "show.credits": "Credits",
  "show.directedBy": "Directed by",
  "show.createdBy": "Created by",
  "show.minutes": "{{minutes}} min",
  "show.titleOverline": "Title",
  "show.loading": "Loading title",
  "show.error.load": "Couldn't load this title",
  "show.unavailable": "This title isn't available",
  "show.unavailableBody": "It may have been removed from the catalog.",
  "show.episodes": "Episodes",
  "show.seasonsSection": "Seasons",
  "show.jumpToSeason": "Jump to season",
  "show.seasonChip": "Season {{number}}",
  "show.seasonChipCompleted": "Season {{number}}, completed",
  "show.episodesOf": "{{watched}} of {{total}} episodes",
  "show.seasonCompleted": "completed",
  "show.nextEpisode": "Next",
  "show.watchAll": "Watch all",
  "show.unwatchAll": "Unwatch all",
  "show.loadingSeasons": "Loading seasons",
  "show.loadingEpisodes": "Loading episodes",
  "show.error.seasons": "Couldn't load the seasons.",
  "show.error.episodes": "Couldn't load the episodes.",
  "show.noEpisodeDetails": "No episode details yet.",
  "show.markWatchedA11y": "Mark {{label}} watched",
  "show.markUnwatchedA11y": "Mark {{label}} unwatched",
  "show.ratingForA11y": "Your rating for {{label}}",
  "show.discussA11y": "Discuss {{label}}",
  "show.similar": "Similar",
  "show.loadingSimilar": "Loading similar titles",
  "show.tracking.statusLabel": "Status",
  "show.tracking.ratingLabel": "Your rating",
  "show.tracking.ratingValueA11y": "Your rating: {{rating}} of 5",
  "show.tracking.addToLibrary": "Add to library",
  "show.tracking.removeFromLibrary": "Remove from library",
  "show.tracking.progressA11y": "Episode progress",
  "show.tracking.ofEpisodes": "of {{total}} episodes",
  "show.tracking.episodesLeft": "{{count}} episodes left",
  "show.tracking.episodesLeft_singular": "{{count}} episode left",
  "show.tracking.status.wantToWatch": "Want to watch",
  "show.tracking.status.watching": "Watching",
  "show.tracking.status.finished": "Finished",
  "show.tracking.status.dropped": "Dropped",
  "show.tracking.status.watched": "Watched",
  "show.tracking.statusA11y": "Status: {{status}}",
  "show.tracking.statusSheetA11y": "Status",
  "show.action.markWatched": "Mark watched",
  "show.action.watchAgain": "Watch again",
  "show.action.markFinished": "Mark finished",
  "show.action.start": "Start {{target}}",
  "show.action.startWatching": "Start watching",
  "show.action.continueLabel": "Continue {{target}}",
  "show.action.continueShort": "Continue",
  "show.trailer.title": "Trailer",
  "show.trailer.play": "Play trailer",
  "show.error.updateStatus": "Couldn't update the status",
  "show.error.markEpisodeWatched": "Couldn't mark the episode watched",
  "show.error.unmarkEpisode": "Couldn't unmark the episode",
  "show.error.markSeasonWatched": "Couldn't mark the season watched",
  "show.error.unmarkSeason": "Couldn't unmark the season",
  "show.error.markFinished": "Couldn't mark the title finished",
  "show.error.startRewatch": "Couldn't start the rewatch",
  "show.error.rate": "Couldn't save the rating",

  // Rating stars
  "component.rating.rated": "Rated {{value}} of 5",
  "component.rating.rateStar": "Rate {{value}} of 5",
  "component.rating.unrated": "Unrated",

  // Discover
  "discover.title": "Discover",
  "discover.searchField": "Search shows and movies",
  "discover.searchScope": "Search scope",
  "discover.scope.all": "All",
  "discover.scope.shows": "Series",
  "discover.scope.movies": "Movies",
  "discover.clear": "Clear",
  "discover.loadingGenres": "Loading genres",
  "discover.browseByGenre": "Browse by genre",
  "discover.searching": "Searching",
  "discover.error.search": "Couldn't search right now",
  "discover.error.load": "Couldn't load Discover",
  "discover.noMatches": "No matches",
  "discover.noMatchesBody":
    "Nothing found for \u201c{{query}}\u201d. Try a different title or browse a genre.",
  "discover.clearSearch": "Clear search",
  "discover.resultCount": "{{count}} results for \u201c{{query}}\u201d",
  "discover.resultCount_singular": "{{count}} result for \u201c{{query}}\u201d",
  "discover.resultCountNoQuery": "{{count}} results",
  "discover.resultCountNoQuery_singular": "{{count}} result",
  "discover.loadingMoreResults": "Loading more results",
  "discover.loadingBrowse": "Loading trending and top rated",
  "discover.browseOverline": "Browse",
  "discover.browseEmpty": "Trending and top-rated titles will appear here.",
  "discover.trending": "Trending",
  "discover.topRated": "Top Rated",
  "discover.recommended": "We think you'll like",
  "discover.scrollTrendingLeft": "Scroll trending left",
  "discover.scrollTrendingRight": "Scroll trending right",
  "discover.scrollTopRatedLeft": "Scroll top rated left",
  "discover.scrollTopRatedRight": "Scroll top rated right",
  "discover.scrollRecommendedLeft": "Scroll recommended left",
  "discover.scrollRecommendedRight": "Scroll recommended right",
  "discover.genreClose": "Close genre",
  "discover.genreOverline": "Genre",
  "discover.titlesCount": "{{count}} titles",
  "discover.titlesCount_singular": "{{count}} title",
  "discover.sortGenreResults": "Sort genre results",
  "discover.sortPopular": "Popular",
  "discover.sortTopRated": "Top rated",
  "discover.error.genre": "Couldn't load this genre",
  "discover.genreEmpty": "No titles in this genre yet.",
  "discover.backToDiscover": "Back to Discover",
  "discover.loadingMoreGenre": "Loading more {{genre}} titles",
  "discover.loadingGenreTitles": "Loading {{genre}} titles",
  "discover.stats.rating": "Rated {{rating}} out of 5",
  "discover.stats.discussionCount": "{{count}} comments",

  // Calendar
  "calendar.eyebrow": "What's airing",
  "calendar.title": "Calendar",
  "calendar.previousMonth": "Previous month",
  "calendar.nextMonth": "Next month",
  "calendar.daysA11y": "Days of {{month}}",
  "calendar.today": "today",
  "calendar.episodesAiring": "{{count}} episodes airing",
  "calendar.episodeAiring": "1 episode airing",
  "calendar.noEpisodesOn": "No episodes on {{day}}",
  "calendar.episodesOn": "{{count}} episodes on {{day}}",
  "calendar.episodeOn": "{{count}} episode on {{day}}",
  "calendar.noEpisodesMineDay": "No episodes from your shows air on this day.",
  "calendar.comingUp": "Coming up",
  "calendar.loading": "Loading your calendar",
  "calendar.emptyBody":
    "New episodes from the shows you track will land here as they air.",
  "calendar.error.load": "Couldn't load your calendar",

  // Friends feed
  "friends.title": "Friends",
  "friends.eyebrow": "See what friends watch",
  "friends.feedSource": "Feed source",
  "friends.fromFriends": "From friends",
  "friends.loadingFeed": "Loading the feed",
  "friends.error.load": "Couldn't load the feed",
  "friends.nothingRecent": "Nothing recent",
  "friends.findPeopleToFollow": "Find people to follow",
  "friends.emptyRecentBody":
    "The people you follow haven\u2019t logged any watches recently. Check back soon.",
  "friends.emptyNoFriendsBody": "Search by username to see what friends are watching.",
  "friends.findPeople": "Find people",
  "friends.nothingTrending": "Nothing is trending yet",
  "friends.trendingEmptyBody":
    "Trending collects the most-upvoted comments on titles in your library — from everyone, not just people you follow. As titles you track heat up, the conversation lands here.",
  "friends.exploreDiscover": "Explore Discover",
  "friends.aFriend": "A friend",
  "friends.openDiscussionFor": "Open discussion for {{title}}",
  "friends.verb.watched": "watched",
  "friends.verb.rewatched": "rewatched",
  "friends.verb.finished": "finished",
  "friends.watchedEpisodesOf": "watched {{count}} episodes of",
  "friends.commentedOn": "commented on",
  "friends.deletedComment": "This comment was deleted.",
  "friends.someone": "Someone",
  "friends.filmMeta": "Film · {{time}}",
  "friends.episodeSummaryMore": "{{summary}} +{{count}} more",

  // Connections
  "connections.people": "People",
  "connections.title": "Connections",
  "connections.listA11y": "Connection list",
  "connections.findPeople": "Find people",
  "connections.kind.followers": "followers",
  "connections.kind.following": "following",
  "connections.kind.requests": "requests",
  "connections.followers": "Followers",
  "connections.following": "Following",
  "connections.requests": "Requests",
  "connections.loading": "Loading {{kind}}",
  "connections.error.load": "Couldn't load {{kind}}",
  "connections.personCount": "{{count}} person in {{kind}}",
  "connections.personCount_plural": "{{count}} people in {{kind}}",
  "connections.empty.followers.title": "No followers yet",
  "connections.empty.followers.body": "When someone follows you, they show up here.",
  "connections.empty.following.title": "You aren\u2019t following anyone yet",
  "connections.empty.following.body": "Follow people to see what they are watching.",
  "connections.empty.requests.title": "No pending requests",
  "connections.empty.requests.body": "When someone asks to follow you, review their request here.",
  "connections.member": "Member",
  "connections.follow": "Follow",
  "connections.followBack": "Follow back",
  "connections.followingState": "Following",
  "connections.unfollow": "Unfollow",
  "connections.unfollowA11y": "Unfollow @{{username}}",
  "connections.followBackA11y": "Follow @{{username}} back",
  "connections.approve": "Approve",
  "connections.decline": "Decline",
  "connections.approveA11y": "Approve follow request from @{{username}}",
  "connections.declineA11y": "Decline follow request from @{{username}}",

  // People search
  "people.title": "Find people",
  "people.searchLabel": "Username or name",
  "people.searchPlaceholder": "Search people",
  "people.searchOverline": "Search",
  "people.idle.title": "Search for people",
  "people.idle.body": "Find friends by username or name to see what they are watching.",
  "people.searchingFor": "Searching for {{term}}",
  "people.error.search": "Couldn't search people",
  "people.noResults": "No people found for \u201c{{term}}\u201d",
  "people.noResultsBody": "Search matches the start of usernames and names — check the spelling and try again.",
  "people.foundCount": "{{count}} person found for {{term}}",
  "people.foundCount_plural": "{{count}} people found for {{term}}",
  "people.followsYou": "Follows you",
  "people.privateBadge": "Private",
  "people.requested": "Requested",

  // User profile (shared shelf)
  "user.viewingStats": "Viewing stats",
  "user.loadingStats": "Loading {{name}}'s stats",
  "user.error.stats": "Couldn't load {{name}}'s stats",
  "user.recentWatches": "Recent watches",
  "user.loadingRecent": "Loading recent watches",
  "user.error.recent": "Couldn't load recent watches",
  "user.nothingLogged": "Nothing logged yet.",
  "user.library": "Library",
  "user.libraryMeta": "{{total}} titles · {{watching}} watching · {{completed}} completed",
  "user.loadingLibrary": "Loading the library",
  "user.error.library": "Couldn't load the library",
  "user.emptyShelf": "This shelf is empty.",
  "user.table.title": "Title",
  "user.table.status": "Status",
  "user.table.progress": "Progress",
  "user.table.rating": "Rating",
  "user.status.removed": "Removed",
  "user.rewatched": "Rewatched ×{{count}}",
  "user.private.lock": "This profile is private",
  "user.private.hint":
    "Only approved followers can see {{name}}'s bio, stats, activity, and library.",
  "user.error.load": "Couldn't load this profile",
  "user.notFound": "User not found",
  "user.notFoundBody": "This account doesn't exist, or the username may have changed.",
  "user.followersCount": "followers",
  "user.followingCount": "following",
  "user.loadingProfile": "Loading profile",

  // Profile (own)
  "profile.overline": "Your card",
  "profile.edit": "Edit profile",
  "profile.request": "Request",
  "profile.loading": "Loading your profile",
  "profile.error.load": "Couldn't load your profile",
  "profile.loadingStats": "Loading your stats",
  "profile.error.stats": "Couldn't load your stats",
  "profile.stats.timeWatched": "Time watched",
  "profile.stats.slow": "Still crunching your numbers…",
  "profile.stats.trackedSummary": "{{completed}} completed · {{titles}} tracked",
  "profile.stats.episodesWatched": "Episodes watched",
  "profile.stats.movieViewings": "Movie viewings",
  "profile.stats.seriesWatched": "Series watched",
  "profile.stats.bestStreak": "Best streak (days)",
  "profile.stats.activity": "Viewing activity",
  "profile.stats.activeDays": "{{count}} active days",
  "profile.stats.activeDays_singular": "{{count}} active day",
  "profile.stats.dayStreak": "{{count}} day streak",
  "profile.stats.genres": "Your genres",
  "profile.stats.genresSubtitle": "Share of watch time",
  "profile.stats.estimateNote": "Some missing catalog runtimes use an estimate.",
  "profile.stats.undatedNote": "{{count}} older viewings are in totals but have no day to plot.",
  "profile.stats.undatedNote_singular": "{{count}} older viewing is in totals but has no day to plot.",
  "profile.stats.activityA11y": "Viewing activity over the last year, with {{count}} active days",
  "profile.stats.activityA11y_singular": "Viewing activity over the last year, with {{count}} active day",
  "profile.stats.activityEmpty": "Watch something to start your activity map.",
  "profile.stats.noWatchTime": "no watch time",
  "profile.stats.less": "Less",
  "profile.stats.more": "More",
  "profile.settings": "Settings",
  "profile.appearance": "Appearance",
  "profile.appearanceHint": "The theme applies immediately and persists on this device.",
  "profile.language": "Language",
  "profile.languageHint": "Links and localized content follow this language.",
  "profile.notifications": "Notifications",
  "profile.session": "Session",
  "profile.sessionHint": "Sign out of ScreenAtlas on this device.",
  "profile.signOut": "Sign out",
  "profile.dangerZone": "Danger zone",
  "profile.dangerHint":
    "Deleting your account permanently removes your library, history, and profile. There is no undo.",
  "profile.deleteAccount": "Delete account",
  "profile.privacyPolicy": "Privacy Policy",
  "profile.about": "About",
  "profile.yourComments": "Your comments",
  "profile.commentsEmpty": "You haven't commented yet.",
  "profile.commentsLoading": "Loading your comments",
  "profile.commentsError": "Couldn't load your comments",
  "profile.commentsSeeAll": "See all {{count}}",
  "profile.deleteDialog.description":
    "This permanently deletes your account, your library, and your history. There is no undo.",
  "profile.deleteDialog.typeUsername": "Type @{{username}} or DELETE to confirm",
  "profile.deleteDialog.typeDelete": "Type DELETE to confirm",
  "profile.deleteDialog.queueUnavailable":
    "The deletion queue is unavailable. Nothing was deleted — try again.",
  "profile.deleteDialog.inProgress": "Account deletion is already in progress.",
  "profile.deleteDialog.failed": "Couldn't start the deletion. Try again.",

  // Your data (import/export)
  "profile.yourData": "Your data",
  "profile.yourDataHint": "Take your library with you, or bring a watch history in.",
  "profile.yourData.notAvailable": "Data import and export isn't available right now.",
  "profile.yourData.export": "Export your data",
  "profile.yourData.exporting": "Building your export…",
  "profile.yourData.exportQueued":
    "We'll email you a download link, and it'll appear here too.",
  "profile.yourData.download": "Download",
  "profile.yourData.emailed": "We've also emailed you this link.",
  "profile.yourData.exportFailed": "Couldn't build your export. Try again.",
  "profile.yourData.exportRateLimited": "You've hit the daily export limit. Try again later.",
  "profile.yourData.exportInProgress": "An export is already running — give it a moment.",
  "profile.yourData.import": "Import",
  "profile.yourData.importHint":
    "Bring in your watch history from TV Time, Trakt, Letterboxd, Simkl, a CSV, or a ScreenAtlas export.",
  "profile.yourData.importing": "Importing…",
  "profile.yourData.detecting": "Detecting format…",
  "profile.yourData.uploading": "Uploading… {{percent}}%",
  "profile.yourData.goToLibrary": "Go to your library",
  "profile.yourData.unmatched": "Download unmatched ({{count}})",
  "profile.yourData.duplicate": "This file was already imported — nothing new was added.",
  "profile.yourData.importFailed": "Couldn't import that file. Try again.",
  "profile.yourData.importRateLimited": "You've hit the daily import limit. Try again later.",
  "profile.yourData.importInProgress": "An import is already in progress — give it a moment.",
  "profile.yourData.importPending": "You have an import waiting to start.",
  "profile.yourData.resume": "Resume",
  "profile.yourData.recentReady": "Your export from {{time}} is ready",
  "profile.yourData.recentHint": "This download link works for 7 days.",

  // Import wizard (choose a source → upload → process → done)
  "profile.import.source.title": "Where is your data from?",
  "profile.import.source.tvtime.name": "TV Time",
  "profile.import.source.tvtime.upload":
    "the .zip you downloaded from TV Time (or its tracking CSV)",
  "profile.import.source.trakt.name": "Trakt",
  "profile.import.source.trakt.upload": "the .zip from Settings → Data → Export",
  "profile.import.source.letterboxd.name": "Letterboxd",
  "profile.import.source.letterboxd.upload": "the .zip from Settings → Import & Export (Pro)",
  "profile.import.source.simkl.name": "Simkl",
  "profile.import.source.simkl.upload": "the .zip/.json backup from Settings → Backup",
  "profile.import.source.screenatlas.name": "ScreenAtlas export",
  "profile.import.source.screenatlas.upload": "a .json export from ScreenAtlas",
  "profile.import.source.generic.name": "Other CSV",
  "profile.import.source.generic.upload":
    "a CSV with title, season, episode (and optional date/rating), or title + year for movies",
  "profile.import.source.notSure": "I'm not sure",
  "profile.import.upload.hint": "Up to 50 MB — we'll detect the format automatically.",
  "profile.import.upload.choose": "Choose file",
  "profile.import.upload.guide": "How to export from {{source}}",
  "profile.import.help.tvtime":
    "Open TV Time and go to Settings.\nChoose Export data and download the .zip.\nOr use your tracking CSV instead.",
  "profile.import.help.trakt":
    "Open Trakt and go to Settings.\nChoose Data → Export.\nDownload the .zip file.",
  "profile.import.help.letterboxd":
    "Open Letterboxd and go to Settings.\nChoose Import & Export (Pro).\nDownload your data as a .zip.",
  "profile.import.help.simkl":
    "Open Simkl and go to Settings.\nChoose Backup.\nDownload the .zip or .json backup.",
  "profile.import.help.screenatlas":
    "Download a ScreenAtlas .json export from the app or this website.",
  "profile.import.help.generic":
    "A CSV with a title column — include season and episode for shows, or a year for movies.",
  "profile.import.detected": "Detected format: {{format}}",
  "profile.import.hintMismatch":
    "This looked like a {{format}} file, so we imported it as that.",
  "profile.import.count.shows": "shows",
  "profile.import.count.movies": "movies",
  "profile.import.count.episodes": "episodes",
  "profile.import.count.ratings": "ratings",
  "profile.import.count.watchlist": "watchlist",
  "profile.import.count.rewatches": "rewatches",
  "profile.import.report.title": "Couldn't match everything",
  "profile.import.report.show": "show",
  "profile.import.report.movie": "movie",
  "profile.import.report.rows": "{{count}} rows",
  "profile.import.report.download": "Download report",
  "profile.import.again": "Import another file",
  "profile.import.failUnsupported": "We couldn't recognize that file format.",
  "profile.import.failArchive": "That archive couldn't be read — it may be invalid or too large.",
  "profile.import.failTooLarge": "That file has too many rows to import.",
  "profile.import.failSourceKey": "The upload didn't complete properly. Try again.",

  // Notification preferences
  "notif.prefs.hint": "Choose which comment activity reaches you.",
  "notif.prefs.mentions": "Mentions",
  "notif.prefs.mentionsHint": "Someone mentions me in a comment",
  "notif.prefs.replies": "Replies",
  "notif.prefs.repliesHint": "Someone replies to my comment",
  "notif.prefs.saving": "Saving…",
  "notif.prefs.saved": "Saved",
  "notif.prefs.error": "Couldn\u2019t save — changes reverted.",
  "notif.prefs.loadError": "Couldn't load notification preferences.",
  "notif.prefs.saveError": "Couldn't save notification preferences",

  // Edit profile
  "profile.edit.back": "Profile",
  "profile.edit.title": "Edit profile",
  "profile.edit.loading": "Loading the editor",
  "profile.edit.name": "Name",
  "profile.edit.nameHint": "Up to {{count}} characters. It shows next to @{{username}} in search.",
  "profile.edit.saveName": "Save name",
  "profile.edit.bio": "Bio",
  "profile.edit.bioHint": "Up to {{count}} characters.",
  "profile.edit.saveBio": "Save bio",
  "profile.edit.saveError": "Couldn't save changes. Try again.",
  "profile.socials.title": "Social links",
  "profile.socials.hint": "Optional. They show on your profile. Paste a @handle or a profile link.",
  "profile.socials.instagram": "Instagram",
  "profile.socials.tiktok": "TikTok",
  "profile.socials.x": "X",
  "profile.socials.invalid": "That isn't a valid {{network}} username.",
  "profile.socials.open": "{{network}}: @{{handle}}",
  "profile.socials.save": "Save social links",
  "profile.socials.saveError": "Couldn't save your social links",
  "profile.edit.username": "Username",
  "profile.edit.usernameHint": "You can change your username once per hour.",
  "profile.edit.cooldown": "You can change your username again at {{date}}.",
  "profile.edit.usernameRules": "Use 3–20 characters: a–z, 0–9, or _",
  "profile.edit.checking": "Checking…",
  "profile.edit.available": "That username is available",
  "profile.edit.taken": "That username is taken",
  "profile.edit.reserved": "That username is reserved",
  "profile.edit.cooldownState": "That username is in cooldown",
  "profile.edit.checkError": "Couldn't check right now. Try again.",
  "profile.edit.changeUsername": "Change username",
  "profile.edit.confirmTitle": "Change your username?",
  "profile.edit.confirmBody":
    "Your old @{{username}} links will stop working. You can't change your username again for 1 hour.",
  "profile.edit.renameTaken": "That name was just taken. Try another.",
  "profile.edit.renameError": "Couldn't change username. Try again.",
  "profile.edit.photo": "Profile photo",
  "profile.edit.photoHint":
    "Shown on your card, next to your comments, and to people who follow you.",
  "profile.edit.photoPreview": "Preview of the selected photo",
  "profile.edit.choosePhoto": "Choose a profile photo",
  "profile.edit.changePhoto": "Change photo",
  "profile.edit.uploading": "Uploading…",
  "profile.edit.photoFormat": "JPEG, PNG, or WebP up to 5 MB.",
  "profile.edit.uploadPhoto": "Upload photo",
  "profile.edit.wrongType": "Choose a JPEG, PNG, or WebP image.",
  "profile.edit.tooLarge": "That image is larger than 5 MB. Choose a smaller one.",
  "profile.edit.uploadError": "Couldn't change the photo. Try again.",

  // Discussion
  "discussion.title": "Discussion",
  "discussion.sortBy": "Sort comments",
  "discussion.sortTop": "Top",
  "discussion.sortNew": "New",
  "discussion.startConversation": "Start the conversation",
  "discussion.emptyBody": "No comments yet — be the first to say what you think.",
  "discussion.error.load": "Couldn't load the discussion",
  "discussion.loading": "Loading the discussion",
  "discussion.deleteTitle": "Delete this comment?",
  "discussion.deleteBody": "This can't be undone.",
  "discussion.deletedAuthor": "[deleted]",
  "discussion.you": "You",
  "discussion.edited": "(edited)",
  "discussion.profileA11y": "{{name}}'s profile",
  "discussion.upvote": "Upvote ({{count}})",
  "discussion.removeUpvote": "Remove upvote ({{count}})",
  "discussion.reply": "Reply",
  "discussion.optionsFor": "Options for @{{username}}'s comment",
  "discussion.optionsForThis": "Options for this comment",
  "discussion.edit": "Edit",
  "discussion.report": "Report",
  "discussion.hideReplies": "Hide replies",
  "discussion.repliesCount": "{{count}} reply",
  "discussion.repliesCount_plural": "{{count}} replies",
  "discussion.error.replies": "Couldn't load the replies.",
  "discussion.loadingMoreReplies": "Loading more replies…",
  "discussion.loadMoreReplies": "Load more replies",
  "discussion.editComment": "Edit comment",
  "discussion.attachingImage": "Attaching image…",
  "discussion.imageA11y": "View attached image full size",
  "discussion.replyingTo": "Replying to",
  "discussion.thisComment": "this comment",
  "discussion.cancelReply": "Cancel reply",
  "discussion.replyToA11y": "Reply to {{target}}",
  "discussion.writeCommentA11y": "Write a comment",
  "discussion.replyPlaceholder": "Write a reply…",
  "discussion.composerPlaceholder": "Add to the discussion…",
  "discussion.mentionSuggestions": "People to mention",
  "discussion.post": "Post",
  "discussion.imagePreview": "Selected image preview",
  "discussion.removeImage": "Remove image",
  "discussion.attachImage": "Attach an image",
  "discussion.chooseImage": "Choose an image file",
  "discussion.imageTooLarge": "The image must be 5 MB or smaller.",
  "discussion.reportTitle": "Report this comment",
  "discussion.reportBody": "A moderator will take a look.",
  "discussion.reasonLegend": "Reason",
  "discussion.reason.spoiler": "Spoiler",
  "discussion.reason.spam": "Spam",
  "discussion.reason.harassment": "Harassment",
  "discussion.reason.abuse": "Abuse",
  "discussion.reason.other": "Something else",
  "discussion.reportSubmitted": "Report submitted — thanks for flagging it.",
  "discussion.error.episode": "Couldn't load this episode",
  "discussion.episodeUnavailable": "This episode isn't available",
  "discussion.episodeUnavailableBody": "Check the address and try again.",
  "discussion.loadingEpisode": "Loading episode",
  "discussion.episodeFallback": "Episode {{number}}",
  "discussion.error.post": "Couldn't post the comment",
  "discussion.error.postReply": "Couldn't post the reply",
  "discussion.error.attachImage": "Couldn't attach the image",
  "discussion.error.vote": "Couldn't update the vote",
  "discussion.error.edit": "Couldn't save the edit",
  "discussion.error.report": "Couldn't submit the report",

  // Notifications
  "notif.overline": "Activity",
  "notif.title": "Notifications",
  "notif.unreadCount": "{{count}} unread",
  "notif.markAllRead": "Mark all read",
  "notif.allRead": "All notifications marked as read",
  "notif.loading": "Loading notifications",
  "notif.error.load": "Couldn't load notifications",
  "notif.empty": "You're all caught up",
  "notif.emptyBody":
    "When someone follows you, asks to follow you, or replies to your comments, it lands here.",
  "notif.follow": "started following you",
  "notif.followRequest": "requested to follow you",
  "notif.approved": "approved your follow request",
  "notif.declined": "declined your follow request",
  "notif.resolved": "follow request resolved",
  "notif.commentReply": "replied to your comment",
  "notif.commentMention": "mentioned you in a comment",
  "notif.upvotedComment": "liked your comment",
  "notif.generic": "New activity",
  "notif.unreadBadge": "Unread",
  "notif.approving": "Approving…",
  "notif.declining": "Declining…",
  "notif.approvedState": "Approved",
  "notif.declinedState": "Declined",
  "notif.approveA11y": "Approve follow request from {{name}}",
  "notif.declineA11y": "Decline follow request from {{name}}",
  "notif.markReadError": "Couldn't mark notifications read",
  "notif.error.generic": "Something went wrong",

  // Lists
  "lists.overline": "Collections",
  "lists.title": "Lists",
  "lists.newList": "New list",
  "lists.createList": "Create list",
  "lists.loading": "Loading your lists",
  "lists.error.load": "Couldn't load your lists",
  "lists.empty":
    "Group titles into collections — comfort shows, rewatchers, watching with someone — and keep them one tap away.",
  "lists.createFirst": "Create your first list",
  "lists.count": "{{count}} titles",
  "lists.count_singular": "{{count}} title",
  "lists.updatedAt": "Updated {{date}}",
  "lists.moveUp": "Move {{name}} earlier",
  "lists.moveDown": "Move {{name}} later",
  "lists.movedUp": "Moved {{name}} earlier",
  "lists.movedDown": "Moved {{name}} later",
  "lists.limitReached": "List limit reached — delete one to make room.",
  "lists.nameLimit": "Names are limited to 50 characters.",
  "lists.createError": "Couldn't create the list. Try again.",
  "lists.overSingle": "Collection",
  "lists.addTitles": "Add titles",
  "lists.actionsA11y": "List actions for {{name}}",
  "lists.rename": "Rename",
  "lists.deleteList": "Delete list",
  "lists.renameTitle": "Rename list",
  "lists.deleteTitle": "Delete list?",
  "lists.deleteBody": "\u201c{{name}}\u201d will be deleted. The titles in it stay in your library.",
  "lists.deleting": "Deleting…",
  "lists.loadingDetail": "Loading the list",
  "lists.emptyTitle": "Empty list",
  "lists.emptyBody": "Nothing here yet — add shows and films to start this collection.",
  "lists.notFound": "List not found",
  "lists.notFoundBody": "This list does not exist anymore — it may have been deleted.",
  "lists.backToLists": "Back to Lists",
  "lists.error.loadDetail": "Couldn't load this list",
  "lists.actionsFor": "Actions for {{title}}",
  "lists.moveEarlier": "Move earlier",
  "lists.moveLater": "Move later",
  "lists.removeFromList": "Remove from list",
  "lists.movedEarlier": "Moved {{title}} earlier",
  "lists.movedLater": "Moved {{title}} later",
  "lists.removed": "Removed {{title}} from the list",
  "lists.loadMore": "Load more",
  "lists.renameError": "Couldn't rename the list. Try again.",
  "lists.name": "Name",
  "lists.namePlaceholder": "Comfort shows",
  "lists.nameRequired": "Give the list a name.",
  "lists.description": "Description",
  "lists.descriptionPlaceholder": "A note only you will see",
  "lists.saving": "Saving…",
  "lists.addTo": "Add to {{name}}",
  "lists.searchTitles": "Search titles",
  "lists.searchPlaceholder": "Search shows and films",
  "lists.added": "Added {{title}} to {{list}}",
  "lists.removedFrom": "Removed {{title}} from {{list}}",
  "lists.removeFromA11y": "Remove {{title}} from {{list}}",
  "lists.addToA11y": "Add {{title}} to {{list}}",
  "lists.inList": "In list",
  "lists.add": "Add",
  "lists.searchHint":
    "Search the catalog and tap Add — titles already in this collection are marked.",
  "lists.noMatches": "No matches for \u201c{{query}}\u201d. Try another spelling, or a cast name.",
  "lists.error.rename": "Couldn't rename the list",
  "lists.error.delete": "Couldn't delete the list",
  "lists.error.reorder": "Couldn't reorder your lists",
  "lists.error.reorderShows": "Couldn't reorder the list",
  "lists.error.add": "Couldn't add the title",
  "lists.error.remove": "Couldn't remove the title",

  // Social follow toasts
  "social.error.follow": "Couldn't follow @{{username}}",
  "social.error.unfollow": "Couldn't unfollow @{{username}}",
  "social.error.approve": "Couldn't approve the request",
  "social.error.decline": "Couldn't decline the request",

  // Auth — threshold layout (shared)
  "auth.marquee.title": "Everything you watch, on one shelf.",
  "auth.marquee.body": "Series, films and every episode in between, in sync across the web, iPhone and Android.",
  "auth.homeLink": "ScreenAtlas home",
  "auth.orEmail": "or use email",
  "auth.showPassword": "Show password",
  "auth.hidePassword": "Hide password",
  "auth.google": "Google",
  "auth.apple": "Apple",
  "auth.pw.title": "Your password needs",
  "auth.pw.length": "8 or more characters",
  "auth.pw.upper": "An uppercase letter",
  "auth.pw.lower": "A lowercase letter",
  "auth.pw.digit": "A number",
  "auth.pw.met": "done",
  "auth.pw.unmet": "missing",
  "login.heading": "Welcome back",
  "login.lede": "Sign in to pick up where you left off.",
  "signup.heading": "Create your free account",
  "signup.lede": "Keep track of every show and film you watch.",
  "signup.legalBefore": "By creating an account you agree to the",
  "signup.legalLink": "privacy policy",
  "signup.privacyHref": "/privacy",
  "confirm.resendIn": "Resend in {{count}}s",
  "confirm.differentEmail": "Use a different email",
  "forgot.lede": "We'll email you a 6-digit code to set a new one.",
  "forgot.stepOf": "Step {{step}} of 2",

  // Auth — login
  "login.signIn": "Sign in",
  "login.email": "Email",
  "login.password": "Password",
  "login.forgotPasswordLink": "Forgot password?",
  "login.newToScreenAtlas": "New to ScreenAtlas?",
  "login.createAccount": "Create an account",
  "login.continueWithGoogle": "Continue with Google",
  "login.continueWithApple": "Continue with Apple",
  "login.signedInAs": "Signed in as",
  "login.continueToApp": "Continue to the app",
  "login.signOut": "Sign out",
  "login.challengeBefore": "This account still needs a new password set. Use",
  "login.challengeLink": "forgot password",
  "login.challengeAfter": "to set one, then sign in again.",
  "login.error.enterEmail": "Enter your email.",
  "login.error.enterPassword": "Enter your password.",
  "login.error.invalid": "Invalid email or password.",
  "login.error.network": "Can't reach the server. Check your connection and try again.",
  "login.error.unknown": "Something went wrong. Try again.",

  // Auth — signup
  "signup.title": "Create account",
  "signup.emailValid": "Enter a valid email address.",
  "signup.emailExists": "An account with this email already exists.",
  "signup.weakPassword":
    "That password doesn't meet the requirements. Use 8+ characters with an uppercase letter, a lowercase letter and a number.",
  "signup.haveAccount": "Already have an account?",
  "signup.perksLabel": "What you get",
  "signup.perk.free": "Free on the web, iPhone and Android",
  "signup.perk.search": "Search any show or movie",
  "signup.perk.library": "One library: watching, watchlist, completed",
  "signup.perk.episodes": "Check off episodes and never lose your place",

  // Auth — confirm
  "confirm.title": "Check your email",
  "confirm.codeSent": "We sent a 6-digit code to {{email}}.",
  "confirm.codeSentBefore": "We sent a 6-digit code to",
  "confirm.noEmail": "Enter the email you signed up with and the 6-digit code we sent you.",
  "confirm.code": "Confirmation code",
  "confirm.resend": "Resend code",
  "confirm.submit": "Confirm",
  "confirm.enterEmail": "Enter your email address.",
  "confirm.enterCode": "Enter the 6-digit code.",
  "confirm.success": "Account confirmed. Sign in to continue.",
  "confirm.codeFail": "That code did not work. Try again.",
  "confirm.resendSuccess": "A new code is on its way.",
  "confirm.resendFail": "Could not resend the code.",

  // Auth — forgot
  "forgot.title": "Reset your password",
  "forgot.prompt": "Enter your account email and we will send you a reset code.",
  "forgot.sendCode": "Send reset code",
  "forgot.resetCode": "Reset code",
  "forgot.newPassword": "New password",
  "forgot.setNewPassword": "Set new password",
  "forgot.success": "Password updated. Sign in with your new password.",
  "forgot.resetFail": "Could not reset the password.",
  "forgot.remembered": "Remembered it after all?",

  // Auth callback
  "auth.callback.failedTitle": "Sign-in did not complete",
  "auth.callback.failedBody":
    "Something went wrong while finishing your social sign-in. Please try again.",
  "auth.callback.back": "Back to sign in",
  "auth.callback.signingIn": "Signing you in…",

  // 404
  "error404.title": "Page not found",
  "error404.body": "That page does not exist. Your library is one tap away.",

  // E — friends/social/notif
  "connections.backToProfile": "Back to profile",
  "friends.results": "{{count}} result",
  "friends.results_plural": "{{count}} results",

  // D — library/discover/show/discussion
  "library.count": "{{count}} titles",
  "library.count_singular": "{{count}} title",
  "library.summary.watching": "{{count}} watching",
  "library.summary.completed": "{{count}} completed",
  "library.summary.completed_singular": "{{count}} completed",
  "library.summary.watchlist": "{{count}} to watch",
  "show.tracking.regionA11y": "Your progress",
  "show.scrollSimilarLeft": "Scroll similar left",
  "show.scrollSimilarRight": "Scroll similar right",
  "episode.code": "S{{season}} · E{{episode}}",

  // F — profile/calendar/lists
  "calendar.empty": "Nothing scheduled right now",
  "lists.noLists": "No lists yet",

  // T2 — inbox
  "inbox.title": "Discussions",
  "inbox.tabsLabel": "Conversations to show",
  "inbox.tab.replies": "Replies",
  "inbox.tab.mentions": "Mentions",
  "inbox.tab.yours": "Yours",
  "inbox.tab.friends": "Friends",
  "inbox.tabUnread": ", {{count}} new",
  "inbox.listLabel": "{{tab}} conversations",
  "inbox.loading": "Loading conversations",
  "inbox.error.list": "Couldn't load your conversations",
  "inbox.empty.replies.title": "No replies yet",
  "inbox.empty.replies.body": "When someone answers one of your comments, it shows up here.",
  "inbox.empty.mentions.title": "No mentions yet",
  "inbox.empty.mentions.body": "When someone @mentions you in a comment, it shows up here.",
  "inbox.empty.yours.title": "You haven't commented yet",
  "inbox.empty.yours.body": "Your comments on series, films and episodes collect here, so you can follow the replies.",
  "inbox.empty.friends.title": "Nothing from friends yet",
  "inbox.empty.friends.body": "Recent comments from the people you follow show up here.",
  "inbox.empty.goTonight": "See what's on tonight",
  "inbox.empty.findPeople": "Find people to follow",
  "inbox.pick.title": "Pick a conversation",
  "inbox.pick.body": "Choose a reply or a thread on the left to read it here.",
  "inbox.thread.loading": "Loading the thread",
  "inbox.thread.missingTitle": "This conversation isn't here",
  "inbox.thread.missingBody": "It may be further down the list, or the comment is gone.",
  "inbox.thread.unknownTitle": "This thread can't be opened here",
  "inbox.thread.unknownBody": "The notification doesn't say which discussion it belongs to.",
  "inbox.backToList": "Discussions",
  "inbox.kicker.discussion": "Discussion",
  "inbox.kicker.show": "Series discussion",
  "inbox.kicker.movie": "Film discussion",
  "inbox.kicker.season": "Season {{season}}",
  "inbox.seasonCode": "S{{season}}",
  "inbox.openEpisode": "Episode page",
  "inbox.openTitle": "Title page",
  "inbox.aDiscussion": "a discussion",
  "inbox.unread": "New:",
  "inbox.withImage": "With an image",
  "inbox.snippet.image": "Shared an image",
  "inbox.snippet.replyDeleted": "Reply deleted",
  "inbox.snippet.commentDeleted": "Comment deleted",
  "inbox.snippet.hidden": "Hidden by moderation",
  "inbox.snippet.replied": "Replied to your comment",
  "inbox.snippet.mentioned": "Mentioned you in a comment",
  "notif.emptyPeopleBody": "When someone follows you or asks to follow you, it lands here. Replies and mentions live in Discussions.",
  "notif.openDiscussions": "Open Discussions",
  "discussion.anchorMissing": "Couldn't find that comment here — it may have been deleted or moderated.",
  "discussion.anchorA11y": "(the comment you opened)",

  // T1 — tonight/nav/palette
  "nav.tonight": "Tonight",
  "nav.discussions": "Discussions",
  "nav.calendar": "Calendar",
  "nav.notifications": "Notifications",
  "nav.notificationsUnread": "Notifications, {{count}} unread",
  "nav.unreadReplies": "{{label}}, {{count}} unread replies",
  "nav.search": "Search",
  "nav.searchA11y": "Search titles, people and lists ({{shortcut}})",
  "nav.searchField": "Search titles, people, lists",
  "nav.searchTooltip": "Search {{shortcut}}",
  "nav.collapse": "Collapse sidebar",
  "nav.expand": "Expand sidebar",
  "nav.shelves": "Your shelves",
  "nav.shelfCount": "{{label}}, {{count}} titles",
  "nav.listCount": "{{name}}, {{count}} titles",
  "nav.allLists": "All lists",
  "nav.upNextA11y": "Up next: {{title}}, {{code}}",
  "nav.upNextTitleA11y": "Up next: {{title}}",
  "nav.shortcuts": "Keyboard shortcuts",
  "nav.shortcutsDescription": "Single-key shortcuts are off while you type in a field.",
  "nav.shortcutSearch": "Search titles, people and lists",
  "nav.shortcutCollapse": "Collapse or expand the sidebar",
  "nav.shortcutEpisodes": "On an episode list",
  "tonight.greeting.morning": "{{day}} morning",
  "tonight.greeting.afternoon": "{{day}} afternoon",
  "tonight.greeting.evening": "{{day}} evening",
  "tonight.hello.morning": "Good morning, {{name}}",
  "tonight.hello.afternoon": "Good afternoon, {{name}}",
  "tonight.hello.evening": "Good evening, {{name}}",
  "tonight.upNext": "Up next",
  "tonight.upNextEmpty": "Nothing in progress yet",
  "tonight.upNextEmptyBody": "Start a series and its next episode waits for you here.",
  "tonight.exploreDiscover": "Explore Discover",
  "tonight.loadingUpNext": "Loading what's next",
  "tonight.openShow": "Open show",
  "tonight.alsoInProgress": "Also in progress",
  "tonight.alsoScrollLeft": "Scroll Also in progress left",
  "tonight.alsoScrollRight": "Scroll Also in progress right",
  "tonight.stream": "From your friends",
  "tonight.newItems": "{{count}} new",
  "tonight.newItemsA11y": "{{count}} new from friends. Use the button at the top of the feed to show them.",
  "tonight.loadingFeed": "Loading your friends' activity",
  "tonight.empty.noFriends": "Tonight gets better with friends",
  "tonight.empty.noFriendsBody": "Follow people you know to see what they watch, rate and say about it, right here.",
  "tonight.empty.quiet": "Nothing new from friends yet",
  "tonight.empty.quietBody": "When the people you follow watch, rate or comment, it shows up here.",
  "tonight.findPeople": "Find people",
  "tonight.sidebar": "Right now",
  "tonight.watchingNow": "Friends watching now",
  "tonight.loadingWatching": "Loading who's watching",
  "tonight.watchingA11y": "{{name}} is watching {{title}}",
  "tonight.airingThisWeek": "Airing this week",
  "tonight.loadingAiring": "Loading this week's episodes",
  "tonight.nothingAiring": "Nothing you track airs in the next seven days.",
  "tonight.seeCalendar": "Calendar",
  "tonight.hotOnShelf": "Hot on your shelf",
  "tonight.upvotes": "Upvotes:",
  "tonight.stars": "{{rating}} out of 5 stars",
  "tonight.addWatchlist": "Add to watchlist",
  "tonight.addWatchlistA11y": "Add {{title}} to your watchlist",
  "tonight.inWatchlist": "On your watchlist",
  "tonight.inWatchlistA11y": "{{title}} is on your watchlist",
  "tonight.verb.rated": "rated",
  "tonight.finishedSeries": "finished the series",
  "tonight.watchedEpisodesOf": "watched {{count}} episodes of",
  "tonight.upvoteA11y": "Upvote, {{count}} so far",
  "tonight.reply": "Reply",
  "tonight.replyTo": "Reply to {{name}}",
  "tonight.postReply": "Post reply",
  "tonight.replyPosted": "Reply posted.",
  "tonight.viewThread": "View thread",
  "tonight.viewThreadCount": "View thread ({{count}})",
  "tonight.becauseYouFinished": "Because you finished",
  "tonight.becauseA11y": "Because you finished {{title}}",
  "tonight.error.upNext": "Couldn't load what's next",
  "tonight.error.feed": "Couldn't load your friends' activity",
  "tonight.error.partial": "Some of your friends' activity didn't load.",
  "tonight.error.watching": "Couldn't load who's watching",
  "tonight.error.airing": "Couldn't load this week's episodes",
  "upnext.continue": "Continue",
  "upnext.continueA11y": "Continue {{title}}: mark {{code}} watched",
  "upnext.markWatchedA11y": "Mark {{code}} of {{title}} watched",
  "upnext.codeCompact": "S{{season}}·E{{episode}}",
  "upnext.episodeLong": "Season {{season}}, Episode {{episode}}",
  "upnext.progressOf": "{{watched}} of {{total}}",
  "upnext.progressA11y": "Progress in {{title}}",
  "upnext.caughtUp": "All caught up",
  "upnext.new": "New",
  "palette.title": "Search ScreenAtlas",
  "palette.description": "Find a title, a person or one of your lists, or jump to a page.",
  "palette.inputA11y": "Search titles, people and lists",
  "palette.placeholder": "Search titles, people, lists…",
  "palette.searching": "Searching…",
  "palette.empty": "Nothing matches “{{query}}”.",
  "palette.titles": "Titles",
  "palette.people": "People",
  "palette.lists": "Your lists",
  "palette.recent": "Up next",
  "palette.goTo": "Go to",
  // T3 — library
  "library.heading": "Your library",
  "library.quick.group": "Quick actions for {{title}}",
  "library.quick.rate": "Rate {{title}}",
  "library.quick.rated": "Your rating for {{title}}: {{rating}} of 5",
  "library.quick.rateShort": "Rate",
  "library.quick.status": "Status of {{title}}: {{status}}",
  "library.quick.statusShort": "Status",
  "library.quick.remove": "Remove…",
  "library.quick.removeTitle": "Remove {{title}}?",
  "library.quick.removeBody": "It leaves your library, and its watch progress is cleared.",
  "library.quick.removeConfirm": "Remove",
  "upnext.title": "Up next",
  "upnext.scrollLeft": "Scroll Up next left",
  "upnext.scrollRight": "Scroll Up next right",
  // T4 — discover
  "discover.spotlight.loading": "Loading a pick for you",
  "discover.spotlight.addWatchlist": "Add to watchlist",
  "discover.spotlight.addWatchlistA11y": "Add {{title}} to your watchlist",
  "discover.spotlight.onWatchlist": "On your watchlist",
  "discover.becauseWatched": "Because you watched {{title}}",
  "discover.scrollShelfLeft": "Scroll “{{shelf}}” left",
  "discover.scrollShelfRight": "Scroll “{{shelf}}” right",
  "discover.genres": "Genres",
  "discover.paletteLoadError": "Couldn't open search. Check your connection and try again.",

  // Sharing (docs/sharing-plan.md §11)
  "share.action.a11y": "Share",
  "share.copyLink": "Copy link",
  "share.share": "Share…",
  "share.recommend": "Recommend to a friend…",
  "share.recommendDialog.title": "Recommend to a friend",
  "share.shareOnX": "Share on X",
  "share.shareOnWhatsApp": "Share on WhatsApp",
  "share.notePlaceholder": "Add a note (optional)",
  "share.send": "Send",
  "share.copied": "Link copied",
  "share.recommendedBy": "Recommended by {{name}}",
  "share.error.create": "Couldn't create the share link",
  "share.unavailable": "Link unavailable",
  "share.unavailableBody": "This share link is invalid or no longer available.",
  "share.goToTonight": "Go to Tonight",
  "share.preview.overline": "Shared title",
  "share.preview.recommendation": "{{name}} thinks you'll like {{title}}",
  "share.preview.openTitle": "Open in ScreenAtlas",
  "share.preview.logIn": "Log in",
  "share.preview.createAccount": "Create account",
  "share.preview.openInApp": "Open in app",
  // Profile setup (features/onboarding) — mirrors the app's setup.* copy.
  "setup.title": "Set up your profile",
  "setup.progress": "Step {{step}} of {{total}}",
  "setup.next": "Next",
  "setup.done": "Done",
  "setup.skip": "Skip",
  "setup.language.title": "Choose your language",
  "setup.language.body": "You can change it anytime in Profile settings.",
  "setup.photo.title": "Add a profile photo",
  "setup.photo.body": "Friends spot you faster in the feed and in discussions.",
  "setup.photo.choose": "Choose photo",
  "setup.name.title": "What should we call you?",
  "setup.name.body": "Your name shows on your profile and next to your comments.",
  "setup.username.title": "Pick your username",
  "setup.username.body": "Your current one was generated. A real one makes you easy to find and @mention.",
  "setup.bio.title": "Tell people your taste",
  "setup.bio.body": "A line about what you love to watch.",
  "setup.socials.title": "Where else can people find you?",
  "setup.socials.body": "Optional. Add your Instagram, TikTok or X so friends can follow you there too.",
  "setup.error": "Couldn't save. Try again or skip for now.",
  "setup.replay.title": "Intro",
  "setup.replay.hint": "Walk through profile setup again: language, plus anything still missing.",
  "setup.replay.action": "Replay intro",
  // Redesign 2026-10 — Discussions, Calendar, Profile, Settings, Notifications
  "inbox.verb.replied": "replied to you on",
  "inbox.verb.mentioned": "mentioned you on",
  "inbox.verb.commented": "commented on",
  "inbox.verb.you": "You commented on",
  "nav.settings": "Settings",
  "settings.general": "General",
  "settings.account": "Account",
  "settings.loadingNotifications": "Loading notification preferences",
  "profile.stats.trackedLine": "{{completed}} completed, {{titles}} tracked",
  "user.shelfItemA11y": "{{title}}, {{status}}",
  // Library refresh — toolbar + Filter sheet
  "library.typeA11y": "Type: {{type}}",
  "library.sheet.open": "Filter",
  "library.sheet.title": "Filter and sort",
  "library.sheet.type": "Type",
  "library.sheet.view": "View",
  "library.view.grid": "Grid",
  "library.view.list": "List",

  // Title page redesign + rating prompt
  "show.meta.seasons": "{{count}} seasons",
  "show.meta.seasons_singular": "{{count}} season",
  "show.meta.episodes": "{{count}} episodes",
  "show.meta.episodes_singular": "{{count}} episode",
  "show.synopsis.more": "More",
  "show.synopsis.less": "Less",
  "show.action.addToWatchlist": "Add to watchlist",
  "show.action.rateIt": "Rate it",
  "show.status.chooseA11y": "Choose a status",
  "show.remove.menu": "Remove…",
  "show.remove.title": "Remove {{title}}?",
  "show.remove.body": "It leaves your library, and its watch history is cleared.",
  "show.remove.confirm": "Remove",
  "show.lists.add": "Add to list",
  "show.lists.added": "Added to {{list}}",
  "show.lists.empty": "You have no lists yet",
  "show.lists.manage": "Your lists…",
  "show.lists.loading": "Loading your lists",
  "show.lists.error": "Couldn't load your lists",
  "show.cast": "Cast",
  "show.cast.scrollLeft": "Scroll cast left",
  "show.cast.scrollRight": "Scroll cast right",
  "show.cast.seeAll": "See all cast ({{count}})",
  "show.cast.seeLess": "Show less",
  "show.aside.a11y": "About this title",
  "show.facts.heading": "About",
  "show.facts.status": "Status",
  "show.facts.status.airing": "Returning",
  "show.facts.status.ended": "Ended",
  "show.facts.status.upcoming": "Upcoming",
  "show.facts.network": "Network",
  "show.facts.firstAired": "First aired",
  "show.facts.released": "Released",
  "show.facts.runtime": "Runtime",
  "show.facts.genres": "Genres",
  "show.facts.score": "TMDB score",
  "show.facts.scoreValue": "{{score}} of 5",
  "show.shortcuts.label": "Shortcuts",
  "show.shortcuts.next": "Next episode",
  "show.shortcuts.previous": "Previous episode",
  "show.shortcuts.toggle": "Mark watched or unwatched",
  "ratingPrompt.title": "How was {{title}}?",
  "ratingPrompt.starsA11y": "Rate {{title}}",
  "ratingPrompt.notNow": "Not now",
  "ratingPrompt.rated": "Rated {{count}} stars",
  "ratingPrompt.rated_singular": "Rated {{count}} star",
  "ratingPrompt.undo": "Undo",
  "discover.spotlight.open": "Open",
  "discover.spotlight.reasonFinished": "Because you finished {{title}}",
  "user.shelf.total": "{{count}} titles",
  "user.shelf.watching": "{{count}} watching",
  "user.shelf.completed": "{{count}} completed",
  "discover.searchingFor": "Searching for \u201c{{query}}\u201d\u2026",
  // People pass (profile, shelf, connections, lists)
  "user.followersCount_singular": "follower",
  "profile.commentsMore": "Show more",
  "profile.commentsEmptyAction": "Join a discussion",
  "calendar.day.today": "Today",
  "calendar.day.tomorrow": "Tomorrow",
  "calendar.day.yesterday": "Yesterday",
  "calendar.nextAiring": "Next episode: {{day}}",
  "share.preview.joinPitch": "Keep track of what you watch. Free on the web, iPhone and Android.",
  "auth.error.expiredCode": "That code has expired. Request a new one.",
  "auth.error.tooMany": "Too many attempts. Wait a few minutes and try again.",
  "auth.socialError": "Couldn't start signing in with {{provider}}. Try again.",
  // Discussions / episode thread / notifications UI pass.
  "discussion.newCommentPill": "{{count}} new",
  "discussion.newCommentsPill": "{{count}} new",
  "discussion.newComments": "New comments: {{count}}. Use the button above the comment box to show them.",
  "discussion.loadingMore": "Loading more comments",
  "discussion.writeFirst": "Write the first comment",
  "inbox.empty.openWatching": "Open a show you're watching",
};

export default en;
